import { Document, Packer, Paragraph, TextRun, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import type { BrokerAgreementData } from '@/components/admin/agreements/BrokerAgreementGenerator';

const formatCurrency = (amount: string) => {
  const num = parseFloat(amount || "0");
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

export const generateBrokerAgreementWordDocument = async (data: BrokerAgreementData) => {
  const content = [
    // Title
    new Paragraph({
      children: [
        new TextRun({
          text: "Broker Commission Agreement For Land Contract",
          bold: true,
          size: 22,
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),

    // Opening paragraph
    new Paragraph({
      children: [
        new TextRun({
          text: `I/We, ${data.brokerName}, hereby agree to broker to ${data.investorName}, that certain Land Contract executed between ${data.sellerName} as Seller(s), and ${data.buyerName} as Buyer(s).`,
          size: 20,
        })
      ],
      spacing: { after: 200 }
    }),

    // Contract terms header
    new Paragraph({
      children: [
        new TextRun({
          text: "The terms of a certain Land Contract are listed as follows:",
          size: 20,
        })
      ],
      spacing: { after: 150, before: 150 }
    }),

    // Contract terms as paragraphs
    new Paragraph({
      children: [
        new TextRun({ text: "Date of Sale: ", bold: true, size: 20 }),
        new TextRun({ text: formatDate(data.dateOfSale), size: 20 }),
      ],
      spacing: { after: 80 },
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "Sale Price: ", bold: true, size: 20 }),
        new TextRun({ text: formatCurrency(data.salePrice), size: 20 }),
      ],
      spacing: { after: 80 },
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "Down Payment: ", bold: true, size: 20 }),
        new TextRun({ text: formatCurrency(data.downPayment), size: 20 }),
      ],
      spacing: { after: 80 },
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "Interest Rate: ", bold: true, size: 20 }),
        new TextRun({ text: `${data.interestRate}%`, size: 20 }),
      ],
      spacing: { after: 80 },
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "P&I Payment: ", bold: true, size: 20 }),
        new TextRun({ text: formatCurrency(data.piPayment), size: 20 }),
      ],
      spacing: { after: 80 },
    }),

    new Paragraph({
      children: [
        new TextRun({ text: "Date of First Payment: ", bold: true, size: 20 }),
        new TextRun({ text: formatDate(data.firstPaymentDate), size: 20 }),
      ],
      spacing: { after: 200 },
    }),

    // Agreement paragraph
    new Paragraph({
      children: [
        new TextRun({
          text: `${data.investorName} agrees to buy all of the remaining payments due, with the first payment to ${data.investorName} due on ${formatDate(data.firstPaymentToInvestor)} and you agree to broker the above Land Contract described for the sum of ${formatCurrency(data.netOffer)}, payable at closing. All commissions will be paid out of this amount. You agree to provide to ${data.investorName}, prior to closing, a signed agreement between you and the note seller specifying your cash offer to the seller.`,
          size: 20,
        })
      ],
      spacing: { after: 200, before: 200 }
    }),

    // Net offer breakdown header
    new Paragraph({
      children: [
        new TextRun({
          text: `${data.investorName}'s net offer of ${formatCurrency(data.netOffer)} will be paid at closing in the following manner:`,
          size: 20,
        })
      ],
      spacing: { after: 150 }
    }),

    // Dynamic breakdown items
    ...data.breakdownItems.map((item, index) => 
      new Paragraph({
        children: [
          new TextRun({ 
            text: `${String.fromCharCode(97 + index)}. ${item.label}: `, 
            size: 20 
          }),
          new TextRun({ 
            text: formatCurrency(item.amount), 
            size: 20 
          }),
        ],
        spacing: { after: 80 },
      })
    ),

    new Paragraph({
      spacing: { after: 150 }
    }),

    // Closing paragraphs
    new Paragraph({
      children: [
        new TextRun({
          text: "This Agreement constitutes our entire understanding and may not be changed or modified except by an agreement in writing signed by both parties.",
          size: 20,
        })
      ],
      spacing: { after: 200 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "If the above is in agreement with your understanding, please execute where indicated below and return a copy to this office.",
          size: 20,
        })
      ],
      spacing: { after: 400 }
    }),

    // Signature section
    new Paragraph({
      children: [
        new TextRun({
          text: "AGREED AND ACCEPTED:",
          bold: true,
          size: 20,
        })
      ],
      spacing: { after: 300 }
    }),

    // Broker signature
    new Paragraph({
      children: [
        new TextRun({
          text: `${data.signedByName}`,
          size: 20,
        })
      ],
      spacing: { after: 80 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: `${data.signedByCompany}`,
          size: 20,
        })
      ],
      spacing: { after: 80 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "_".repeat(50),
          size: 20,
        })
      ],
      spacing: { after: 40 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "Signature",
          size: 18,
        })
      ],
      spacing: { after: 80 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: `Date: ${formatDate(data.agreementDate)}`,
          size: 20,
        })
      ],
      spacing: { after: 400 }
    }),

    // Investor signature section
    new Paragraph({
      children: [
        new TextRun({
          text: data.investorName,
          size: 20,
        })
      ],
      spacing: { after: 80 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "_".repeat(50),
          size: 20,
        })
      ],
      spacing: { after: 40 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "Signature",
          size: 18,
        })
      ],
      spacing: { after: 80 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "Date: _______________",
          size: 20,
        })
      ],
    }),
  ];

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: content,
      },
    ],
  });

  return await Packer.toBlob(doc);
};
